import { z } from 'zod';
import { PRIORIDADES, type Prioridade, type W5h2, type W5h2Action } from './strategy';

/* ─────────────────────────────────────────────────────────────────────
   5W2H — o Plano de Ação.

   A IA lê o que as telas do Strategy registraram e propõe as ações
   iniciais: só a prioridade e o "o quê" (e de onde veio cada uma). A
   organização preenche o resto, acrescenta linhas e edita tudo. A tela
   mostra sempre por prioridade.
   ───────────────────────────────────────────────────────────────────── */

export const PRIORITY_LABEL: Record<Prioridade, string> = { alta: 'Alta', media: 'Média', baixa: 'Baixa' };
const RANK: Record<Prioridade, number> = { alta: 0, media: 1, baixa: 2 };

/** Por prioridade; dentro dela, na ordem em que as ações entraram (uma linha não pula ao ser editada). */
export const sortActions = <A extends Pick<W5h2Action, 'priority'>>(xs: A[]) =>
  xs.map((a, i) => ({ a, i })).sort((x, y) => RANK[x.a.priority] - RANK[y.a.priority] || x.i - y.i).map(x => x.a);

/** Ids estáveis (ac-N). */
export function withActionIds(c: W5h2): W5h2 {
  const used = new Set<string>();
  let n = 0;
  return {
    ...c,
    actions: c.actions.map(a => {
      let id = a.id && !used.has(a.id) ? a.id : '';
      while (!id || used.has(id)) id = `ac-${++n}`;
      used.add(id);
      return { ...a, id };
    }),
  };
}

/** A data de hoje (AAAA-MM-DD) no fuso da organização. */
export const localToday = (d = new Date(), tz = 'America/Sao_Paulo') =>
  new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);

/** O prazo passou e a ação não foi concluída. */
export const isOverdue = (a: Pick<W5h2Action, 'when' | 'doneAt'>, today: string) => !a.doneAt && !!a.when && a.when < today;

export function w5h2Metrics(c: W5h2, today = localToday()) {
  const acts = c.actions.filter(a => a.what.trim());
  const open = acts.filter(a => !a.doneAt);
  const by = Object.fromEntries(PRIORIDADES.map(p => [p, acts.filter(a => a.priority === p).length])) as Record<Prioridade, number>;
  const money = acts.filter(a => a.howMuch != null);
  /* a próxima entrega e o que falta preencher: só entre as abertas */
  const next = open.map(a => a.when).filter(w => w && w >= today).sort()[0] ?? null;
  return {
    total: acts.length, by, invest: money.reduce((s, a) => s + (a.howMuch ?? 0), 0), priced: money.length, next,
    open: open.filter(a => !a.who.trim() || !a.when).length,
    done: acts.length - open.length,
    overdue: open.filter(a => isOverdue(a, today)).length,
  };
}

/** "25000", "25.000", "R$ 25 mil" ou "25,5 mil" → número; sem dígitos → sem valor. */
export function parseMoney(s: string): number | null {
  const t = s.toLowerCase();
  const mult = /\bmil\b|\dk\b/.test(t) ? 1000 : 1;
  const n = parseFloat(t.replace(/[^\d,]/g, '').replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * mult) : null;
}

/* ── a proposta da IA ─────────────────────────────────────────────── */

export const w5h2SuggestRequestSchema = z.object({
  /** o que já está no plano ou foi descartado (não repetir) */
  avoid: z.array(z.string().trim().max(240)).max(60).default([]),
});
export const w5h2SuggestionSchema = z.object({
  acoes: z.array(z.object({
    what: z.string().trim().min(6).max(200),
    priority: z.enum(PRIORIDADES),
    fonte: z.string().trim().max(120).default(''),
  })).min(1).max(12),
});
export type W5h2Suggestion = z.infer<typeof w5h2SuggestionSchema>;
export interface W5h2SuggestionDto extends W5h2Suggestion { basic: boolean; note: string | null }
